import { Input, Select, Space } from "antd";

import type { Telefono } from "../../lib/document/model";
import {
  componerTelefono,
  IDIOMAS,
  MODALIDADES,
  NIVELES_FORMACION,
  NIVELES_IDIOMA,
  TIPOS_DISCAPACIDAD,
  PAISES,
  PAIS_POR_DEFECTO,
  separarTelefono,
} from "./catalogos";

/** Los campos con vocabulario cerrado. Todos hablan el mismo contrato: value / onChange. */

const opciones = (valores: readonly string[]) =>
  valores.map((v) => ({ value: v, label: v }));

export const SelectorPais = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Select
    style={{ width: "100%" }}
    placeholder="Selecciona un país"
    value={value || undefined}
    onChange={onChange}
    allowClear
    showSearch
    optionFilterProp="label"
    // Se guarda el NOMBRE, no el código: es lo que ya hay en la columna
    // `country_name` de todas las hojas de vida existentes.
    options={PAISES.map((p) => ({ value: p.nombre, label: `${p.bandera} ${p.nombre}` }))}
  />
);

export const SelectorNivelFormacion = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Select
    style={{ width: "100%" }}
    placeholder="Nivel de estudio"
    value={value || undefined}
    onChange={onChange}
    allowClear
    options={opciones(NIVELES_FORMACION)}
  />
);

export const SelectorModalidad = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Select
    style={{ width: "100%" }}
    placeholder="Presencial, Virtual o Híbrido"
    value={value || undefined}
    onChange={onChange}
    allowClear
    options={opciones(MODALIDADES)}
  />
);

export const SelectorIdioma = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Select
    style={{ width: "100%" }}
    placeholder="Idioma"
    value={value || undefined}
    onChange={onChange}
    showSearch
    // Entrada libre además del catálogo: la lista tiene diez idiomas y alguien
    // puede hablar uno que no esté.
    mode="tags"
    maxCount={1}
    options={opciones(IDIOMAS)}
    onSelect={(v: string) => onChange(v)}
  />
);

export const SelectorNivelIdioma = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Select
    style={{ width: "100%" }}
    placeholder="Nivel"
    value={value || undefined}
    onChange={onChange}
    allowClear
    options={opciones(NIVELES_IDIOMA)}
  />
);

/**
 * Telefono: prefijo de pais separado del numero.
 *
 * Se guarda `{ code, value }` con el `value` COMPLETO, prefijo incluido
 * ("+57 3001234567"), porque es lo que ya hay en la base y lo que el PDF
 * imprime tal cual. Al editar se separa para mostrarlo y se vuelve a componer
 * al guardar.
 */
export const CampoTelefono = ({
  value,
  onChange,
}: {
  value: Telefono | null;
  onChange: (v: Telefono | null) => void;
}) => {
  const codigo = value?.code || PAIS_POR_DEFECTO;
  const nacional = separarTelefono(value?.value ?? "", codigo);

  return (
    <Space.Compact style={{ width: "100%" }}>
      <Select
        style={{ width: 140 }}
        value={codigo}
        showSearch
        optionFilterProp="label"
        onChange={(c) => onChange(componerTelefono(c, nacional))}
        options={PAISES.map((p) => ({
          value: p.codigo,
          label: `${p.bandera} ${p.prefijo}`,
          // El buscador mira el nombre; la etiqueta enseña bandera y prefijo.
          title: p.nombre,
        }))}
        filterOption={(entrada, opcion) =>
          `${opcion?.title ?? ""} ${opcion?.label ?? ""}`
            .toLowerCase()
            .includes(entrada.toLowerCase())
        }
      />
      <Input
        style={{ width: "100%" }}
        placeholder="300 000 0000"
        value={nacional}
        onChange={(e) => onChange(componerTelefono(codigo, e.target.value))}
      />
    </Space.Compact>
  );
};

export const SelectorDiscapacidad = ({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) => (
  <Select
    style={{ width: "100%" }}
    placeholder="Prefiero no especificarlo"
    value={value || undefined}
    onChange={(v) => onChange(v ?? null)}
    allowClear
    options={opciones(TIPOS_DISCAPACIDAD)}
  />
);
